/**
 * 安全与审计。
 *
 * 三块内容：
 *   审计日志    谁在什么时候看了/改了什么。operatorId 不建外键——
 *              审计必须在操作者账号注销后仍然完整保留
 *   内容审核    ModerationDecision，迁移带来 872 条
 *   管理员与 RBAC  当前谁有权限，权限从哪来
 */

import { Alert, Card, Col, Input, Row, Select, Space, Statistic, Table, Tabs, Tag, Typography } from "antd";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../../services/api";
import { DataTable, useTableParams } from "../../components/DataTable";
import { fmtDateTime, fmtNumber, RelativeTime, ShortId, StatusTag, Tags } from "../../components/format";

export function SecurityPage() {
  const [tab, setTab] = useState("audit");
  return (
    <Tabs
      activeKey={tab}
      onChange={setTab}
      items={[
        { key: "audit", label: "审计日志", children: <AuditLogs /> },
        { key: "moderation", label: "内容审核", children: <Moderation /> },
        { key: "admins", label: "管理员", children: <Admins /> },
        { key: "rbac", label: "角色与权限", children: <Rbac /> },
      ]}
    />
  );
}

const AUDIT_FILTERS = ["action", "resourceType", "resourceId", "operatorId"];

function AuditLogs() {
  const { searchParams, setParam } = useTableParams(AUDIT_FILTERS);
  return (
    <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="只读也要留痕"
        description="查看用户私聊（admin.readConversationMessages）与查看 Prompt 原文（admin.readPrompt）都会写审计。查看用户私聊本身就是一次权力行使。写操作接入后，每个写用例都必须在同一事务里追加一条。"
      />
      <DataTable
        path="/api/v1/admin/security/audit-logs"
        filterKeys={AUDIT_FILTERS}
        toolbar={
          <Space wrap>
            <Input.Search
              allowClear
              style={{ width: 260 }}
              placeholder="动作，如 admin.readPrompt"
              defaultValue={searchParams.get("action") ?? ""}
              onSearch={(v) => setParam("action", v)}
            />
            <Input.Search
              allowClear
              style={{ width: 220 }}
              placeholder="资源类型，如 Conversation"
              defaultValue={searchParams.get("resourceType") ?? ""}
              onSearch={(v) => setParam("resourceType", v)}
            />
            <Input.Search
              allowClear
              style={{ width: 300 }}
              placeholder="资源 ID"
              defaultValue={searchParams.get("resourceId") ?? ""}
              onSearch={(v) => setParam("resourceId", v)}
            />
          </Space>
        }
        columns={[
          { title: "动作", dataIndex: "action", width: 260 },
          {
            title: "操作人",
            dataIndex: "operatorName",
            width: 130,
            render: (v: string | null, r: { operatorId: string }) =>
              v ? <Link to={`/users/${r.operatorId}`}>{v}</Link> : r.operatorId,
          },
          { title: "资源类型", dataIndex: "resourceType", width: 140 },
          { title: "资源 ID", dataIndex: "resourceId", width: 110, render: (v) => <ShortId value={v} /> },
          { title: "理由", dataIndex: "reason", render: (v) => v || "—" },
          {
            title: "变更摘要",
            dataIndex: "afterSummary",
            width: 220,
            render: (v: unknown) => (v == null ? "—" : <span className="mono">{JSON.stringify(v)}</span>),
          },
          { title: "requestId", dataIndex: "requestId", width: 110, render: (v) => <ShortId value={v} /> },
          { title: "时间", dataIndex: "createdAt", width: 170, render: fmtDateTime },
        ]}
      />
    </>
  );
}

const MOD_FILTERS = ["verdict", "targetType", "decidedBy", "category"];

function Moderation() {
  const { searchParams, setParam } = useTableParams(MOD_FILTERS);
  const stats = useQuery({
    queryKey: ["moderationStats"],
    queryFn: () =>
      api.get<{
        total: number;
        byVerdict: { verdict: string; count: number }[];
        byActor: { actor: string; count: number }[];
        byTargetType: { targetType: string; count: number }[];
        topCategories: { category: string; count: number }[];
      }>("/api/v1/admin/security/moderation/stats"),
  });

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <Row gutter={[12, 12]}>
        <Col xs={24} lg={8}>
          <Card size="small" title="判定结果" loading={stats.isLoading}>
            <Space wrap>
              {stats.data?.byVerdict.map((v) => (
                <Statistic
                  key={v.verdict}
                  title={v.verdict}
                  value={v.count}
                  valueStyle={{ fontSize: 20, color: v.verdict === "block" ? "#B4232E" : undefined }}
                />
              ))}
            </Space>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card size="small" title="判定方" loading={stats.isLoading}>
            <Space wrap>
              {stats.data?.byActor.map((a) => (
                <Statistic key={a.actor} title={a.actor} value={a.count} valueStyle={{ fontSize: 20 }} />
              ))}
            </Space>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card size="small" title="高频类别" loading={stats.isLoading}>
            <Space wrap size={[4, 4]}>
              {stats.data?.topCategories.slice(0, 12).map((c) => (
                <Tag key={c.category}>
                  {c.category} {fmtNumber(c.count)}
                </Tag>
              ))}
            </Space>
          </Card>
        </Col>
      </Row>

      <DataTable
        path="/api/v1/admin/security/moderation"
        filterKeys={MOD_FILTERS}
        toolbar={
          <Space wrap>
            <Select
              allowClear
              style={{ width: 130 }}
              placeholder="结论"
              value={searchParams.get("verdict") ?? undefined}
              onChange={(v) => setParam("verdict", v)}
              options={["pass", "flag", "block"].map((v) => ({ value: v, label: v }))}
            />
            <Select
              allowClear
              style={{ width: 170 }}
              placeholder="对象类型"
              value={searchParams.get("targetType") ?? undefined}
              onChange={(v) => setParam("targetType", v)}
              options={["message", "asset", "external_source", "memory"].map((v) => ({ value: v, label: v }))}
            />
            <Select
              allowClear
              style={{ width: 150 }}
              placeholder="判定方"
              value={searchParams.get("decidedBy") ?? undefined}
              onChange={(v) => setParam("decidedBy", v)}
              options={["classifier", "rule", "admin"].map((v) => ({ value: v, label: v }))}
            />
            <Input.Search
              allowClear
              style={{ width: 200 }}
              placeholder="类别"
              defaultValue={searchParams.get("category") ?? ""}
              onSearch={(v) => setParam("category", v)}
            />
          </Space>
        }
        columns={[
          { title: "对象类型", dataIndex: "targetType", width: 150 },
          { title: "对象 ID", dataIndex: "targetId", width: 110, render: (v) => <ShortId value={v} /> },
          { title: "结论", dataIndex: "verdict", width: 90, render: (v) => <StatusTag value={v} /> },
          { title: "类别", dataIndex: "categories", render: (v: string[]) => <Tags values={v} /> },
          { title: "置信度", dataIndex: "confidence", width: 90, align: "right", render: (v) => v ?? "—" },
          { title: "判定方", dataIndex: "decidedBy", width: 120 },
          {
            title: "中枢决策",
            dataIndex: "brainRunId",
            width: 110,
            render: (v: string | null) => (v ? <Link to={`/brain/runs/${v}`}>{v.slice(-8)}</Link> : "—"),
          },
          { title: "备注", dataIndex: "note", width: 200, render: (v) => v || "—" },
          { title: "时间", dataIndex: "createdAt", width: 130, render: (v) => <RelativeTime value={v} /> },
        ]}
      />
    </Space>
  );
}

function Admins() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["admins"],
    queryFn: () => api.get<Record<string, unknown>[]>("/api/v1/admin/rbac/admins"),
  });
  if (error) return <Alert type="error" showIcon message={(error as Error).message} />;

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <Alert
        type="info"
        showIcon
        message="管理员就是普通 User 加一行 UserRole"
        description={
          <>
            旧库把 Admin 与 User 拆成两张表，结果是同一个人在两边各有一份资料、两套登录态。新库只有一份身份。
            <br />
            授予 / 回收：<Typography.Text code>pnpm -F @dreamverse/api admin:grant -- --phone 手机号 --role super_admin</Typography.Text>
            ，加 <Typography.Text code>--revoke</Typography.Text> 回收，<Typography.Text code>--list</Typography.Text> 查看。每次操作都写审计。
          </>
        }
      />
      <Card styles={{ body: { padding: 12 } }}>
        <Table
          size="small"
          rowKey={(r: Record<string, unknown>) => `${r["userId"]}-${r["roleKey"]}`}
          loading={isLoading}
          pagination={false}
          dataSource={data ?? []}
          columns={[
            {
              title: "姓名",
              dataIndex: "name",
              width: 140,
              render: (v: string | null, r: Record<string, unknown>) => (
                <Link to={`/users/${r["userId"]}`}>{v || "(未命名)"}</Link>
              ),
            },
            { title: "手机号", dataIndex: "phoneMasked", width: 160 },
            {
              title: "角色",
              dataIndex: "roleKey",
              width: 160,
              render: (v: string, r: Record<string, unknown>) => (
                <Tag color={v === "super_admin" ? "red" : "blue"}>{String(r["roleName"])}</Tag>
              ),
            },
            { title: "账号状态", dataIndex: "status", width: 110, render: (v) => <StatusTag value={v} /> },
            { title: "授予者", dataIndex: "grantedBy", width: 120, render: (v) => v || "—" },
            { title: "授予时间", dataIndex: "grantedAt", width: 170, render: fmtDateTime },
            {
              title: "最后活跃",
              dataIndex: "lastActiveAt",
              width: 140,
              render: (v) => <RelativeTime value={v} />,
            },
          ]}
        />
      </Card>
    </Space>
  );
}

function Rbac() {
  const { data, isLoading } = useQuery({
    queryKey: ["rbacRoles"],
    queryFn: () =>
      api.get<{
        roles: { key: string; displayName: string; description: string | null; memberCount: number; permissions: string[] }[];
        permissions: { key: string; displayName: string; resource: string; action: string }[];
      }>("/api/v1/admin/rbac/roles"),
  });

  return (
    <Row gutter={[12, 12]}>
      <Col xs={24} lg={10}>
        <Card size="small" title="角色" loading={isLoading}>
          <Table
            size="small"
            rowKey="key"
            pagination={false}
            dataSource={data?.roles ?? []}
            expandable={{
              expandedRowRender: (r) => <Tags values={r.permissions} />,
            }}
            columns={[
              { title: "角色", dataIndex: "displayName", width: 120 },
              { title: "key", dataIndex: "key", width: 130 },
              { title: "成员", dataIndex: "memberCount", width: 70, align: "right" },
              {
                title: "权限数",
                dataIndex: "permissions",
                width: 80,
                align: "right",
                render: (v: string[]) => v.length,
              },
            ]}
          />
        </Card>
      </Col>
      <Col xs={24} lg={14}>
        <Card size="small" title="权限点" loading={isLoading}>
          <Table
            size="small"
            rowKey="key"
            pagination={false}
            dataSource={data?.permissions ?? []}
            columns={[
              { title: "key", dataIndex: "key", width: 200, render: (v: string) => <Typography.Text code>{v}</Typography.Text> },
              { title: "名称", dataIndex: "displayName" },
              { title: "资源", dataIndex: "resource", width: 130 },
              { title: "动作", dataIndex: "action", width: 110 },
            ]}
          />
        </Card>
      </Col>
    </Row>
  );
}
